import React from 'react'
import { Link } from 'react-router-dom'
import { Mail, ChevronDown } from 'lucide-react'
import { usePageMeta } from '@/hooks/usePageMeta'

const SUPPORT_EMAIL = 'claritas.support@gmail.com'

const FAQS = [
  {
    q: 'How do I find my parish?',
    a: (
      <>
        In the app, tap the search button on the Home tab. Use your current location to see parishes
        near you on a map or in a list, or search by name, city or address. Tap the star on a parish
        to save it to your parishes.
      </>
    ),
  },
  {
    q: "My parish isn't on Claritas. Can it join?",
    a: (
      <>
        Yes. Ask your parish office to visit our <Link to="/for-parishes" className="text-gold hover:underline">For Parishes</Link> page,
        or email us and we'll reach out to them.
      </>
    ),
  },
  {
    q: 'How do I give to my parish?',
    a: (
      <>
        Open your parish and tap Donate. You can make a one-time gift, set up a recurring weekly or
        monthly gift, or round up everyday purchases. For some parishes, giving happens on our secure
        website instead, and the app takes you there.
      </>
    ),
  },
  {
    q: 'How do I pause or cancel a recurring gift?',
    a: (
      <>
        Open your parish, tap Donate, then choose the Recurring tab. Each recurring gift has Pause and
        Cancel buttons. Cancelling stops future gifts right away.
      </>
    ),
  },
  {
    q: 'Where are my receipts and tax statements?',
    a: (
      <>
        We email a receipt for every successful gift. Each January we also send a year-end giving
        statement, and past statements are available under Donations in your Profile.
      </>
    ),
  },
  {
    q: 'I made a gift by mistake. Can I get a refund?',
    a: (
      <>
        Donations are generally non-refundable. To request a refund, contact the parish you gave to.
        The parish submits the request to Claritas for review, and approved refunds go back to your
        original payment method. See our <Link to="/terms" className="text-gold hover:underline">Terms of Service</Link> for details.
      </>
    ),
  },
  {
    q: 'How do I report content or block someone?',
    a: (
      <>
        Tap the ••• menu on an announcement, parish or attendee to report it or block it. You can
        manage blocked parishes and people from your Profile.
      </>
    ),
  },
  {
    q: 'How do I delete my account?',
    a: (
      <>
        In the app, go to Profile and tap Delete account. This permanently removes your account. Our{' '}
        <Link to="/privacy" className="text-gold hover:underline">Privacy Policy</Link> explains what
        happens to your data.
      </>
    ),
  },
  {
    q: "I'm a parish administrator. How do I update our parish information?",
    a: (
      <>
        Sign in with your administrator account and open the Admin tab in the app, or sign in on this
        website. From there you can edit your parish profile, Mass and confession times, announcements
        and events.
      </>
    ),
  },
]

export default function Support() {
  usePageMeta({
    title: 'Support | Claritas',
    description:
      'Get help with the Claritas app: finding your parish, giving, recurring gifts, receipts, refunds and your account. Contact Claritas support by email.',
  })

  return (
    <div className="min-h-screen bg-background-light">
      {/* Hero */}
      <section className="bg-white py-20 border-b border-surface-border">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-xs font-extrabold tracking-widest uppercase text-gold mb-4">
            Support
          </p>
          <div className="w-8 h-px bg-gold mx-auto mb-6" />
          <h1 className="text-4xl sm:text-5xl font-bold font-display text-charcoal mb-6">
            How Can We Help?
          </h1>
          <p className="text-base text-charcoal-muted max-w-2xl mx-auto mb-8">
            Questions about the app, your gifts or your account? Email us and a member of the
            Claritas team will get back to you.
          </p>
          <a
            href={`mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent('Claritas support request')}`}
            className="inline-flex items-center gap-2 bg-charcoal text-gold font-bold px-6 py-3 rounded hover:bg-charcoal/90 transition-colors duration-150"
          >
            <Mail className="w-4 h-4" />
            Email Support
          </a>
          <p className="text-sm text-charcoal-muted mt-4">
            <a href={`mailto:${SUPPORT_EMAIL}`} className="text-gold hover:underline">
              {SUPPORT_EMAIL}
            </a>
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-background-light py-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <p className="text-xs font-extrabold tracking-widest uppercase text-gold mb-4">
              Common Questions
            </p>
            <h2 className="text-3xl font-bold font-display text-charcoal">
              Frequently Asked Questions
            </h2>
          </div>

          <div className="space-y-3">
            {FAQS.map(({ q, a }) => (
              <details
                key={q}
                className="group bg-white border border-surface-border rounded shadow-card hover:border-gold/30 transition-all duration-150"
              >
                <summary className="flex items-center justify-between gap-4 cursor-pointer list-none p-5 text-base font-bold text-charcoal">
                  {q}
                  <ChevronDown className="w-5 h-5 text-gold flex-shrink-0 transition-transform duration-150 group-open:rotate-180" />
                </summary>
                <p className="px-5 pb-5 text-sm text-charcoal-muted leading-relaxed">{a}</p>
              </details>
            ))}
          </div>

          <p className="text-sm text-charcoal-muted text-center mt-10">
            Still need help? Email{' '}
            <a href={`mailto:${SUPPORT_EMAIL}`} className="text-gold hover:underline">
              {SUPPORT_EMAIL}
            </a>
            .
          </p>
        </div>
      </section>
    </div>
  )
}
